import { useAuthLicenseAddOns } from 'common/features/licenseFeatures/useAuthLicenseAddOns';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

export const communicationTemplates = [
    { templateName: 'access-code', supportedChannels: ['email', 'sms'] },
    { templateName: 'appointment-reminder', supportedChannels: ['email', 'sms'] },
    { templateName: 'appointment-invitation', supportedChannels: ['email', 'sms'] },
    { templateName: 'appointment-rescheduled', supportedChannels: ['email', 'sms'] },
    { templateName: 'appointment-cancellation', supportedChannels: ['email', 'sms'] },
    { templateName: 'satisfaction-survey', supportedChannels: ['email', 'sms'] },
    { templateName: 'appointment-past-pending', supportedChannels: ['email', 'sms'] },
    { templateName: 'appointment-past-pending-agent', supportedChannels: ['email'] },
    { templateName: 'appointment-confirmation', supportedChannels: ['email', 'sms'] },
    { templateName: 'appointment-requested', supportedChannels: ['email', 'sms'] },
    { templateName: 'ievent-invitation', supportedChannels: ['email', 'sms'] },
    { templateName: 'ievent-reminder', supportedChannels: ['email', 'sms'] },
    { templateName: 'ievent-cancellation', supportedChannels: ['email', 'sms'] },
    { templateName: 'ievent-survey', supportedChannels: ['email', 'sms'] },
    { templateName: 'ievent-update', supportedChannels: ['email', 'sms'] },
    { templateName: 'ievent-available-slot', supportedChannels: ['email', 'sms'] },
    { templateName: 'ievent-admin-registration', supportedChannels: ['email', 'sms'] },
    { templateName: 'ievent-broadcast', supportedChannels: ['email', 'sms'] },
    { templateName: 'new-contact-request', supportedChannels: ['email'] },
    { templateName: 'new-lost-opportunity', supportedChannels: ['email'] },
];

const SMS_SUPPORTED_TEMPLATE_NAMES = new Set(
    communicationTemplates.filter((template) => template.supportedChannels.includes('sms')).map((template) => template.templateName),
);

export function useCommunicationType() {
    const { t } = useTranslation();
    const isAuthorize = useAuthLicenseAddOns();
    const iCall = isAuthorize(['oneToOne.iCall']);
    const iEvents = isAuthorize(['oneToOne.iEvents']);
    const iStore = isAuthorize(['oneToOne.iStore']);
    const iLive = isAuthorize(['oneToMany.iLive']);
    const iCalendar = isAuthorize(['oneToOne.iCalendar']);
    const leads = isAuthorize(['leads']);

    const APPOINTMENT_TEMPLATES = useMemo(
        () => [
            {
                name: 'appointment-invitation',
                label: t('templates.appointmentInvitation'),
            },
            {
                name: 'appointment-reminder',
                label: t('templates.appointmentReminder'),
            },
            {
                name: 'appointment-past-pending',
                label: t('templates.appointmentPastPending'),
            },
            {
                name: 'appointment-past-pending-agent',
                label: t('templates.appointmentPastPendingAgent'),
            },
            {
                name: 'appointment-cancellation',
                label: t('templates.appointmentCancellation'),
            },
            {
                name: 'satisfaction-survey',
                label: t('templates.satisfactionSurvey'),
            },
            {
                name: 'access-code',
                label: t('templates.accessCode'),
            },
            {
                name: 'appointment-rescheduled',
                label: t('templates.appointmentRescheduled'),
            },
            {
                name: 'appointment-confirmation',
                label: t('templates.appointmentConfirmation'),
            },
            {
                name: 'appointment-requested',
                label: t('templates.appointmentRequested'),
            },
        ],
        [t],
    );

    const EMAIL_TEMPLATES = useMemo(
        () => [
            ...(iLive
                ? [
                      {
                          name: 'subscription-event-date-updated',
                          label: t('templates.subscriptionEventDateUpdated'),
                      },
                      {
                          name: 'subscription-event-cancelled',
                          label: t('templates.subscriptionEventCancelled'),
                      },
                      {
                          name: 'channel-subscription',
                          label: t('templates.channelSubscription'),
                      },
                      {
                          name: 'channel-subscription-new-event',
                          label: t('templates.channelSubscriptionNewEvent'),
                      },
                      {
                          name: 'channel-subscription-new-shoppable',
                          label: t('templates.channelSubscriptionNewShoppable'),
                      },
                  ]
                : []),

            ...(iLive || iCall
                ? [
                      {
                          name: 'event-invitation',
                          label: t('templates.eventInvitation'),
                      },
                      {
                          name: 'event-subscription',
                          label: t('templates.eventSubscription'),
                      },
                      {
                          name: 'event-subscription-reminder',
                          label: t('templates.eventSubscriptionReminder'),
                      },
                      {
                          name: 'event-cancellation',
                          label: t('templates.eventCancellation'),
                      },
                      {
                          name: 'event-finalized',
                          label: t('templates.eventFinalized'),
                      },
                      {
                          name: 'game-winners',
                          label: t('templates.eventGameWinners'),
                      },
                  ]
                : []),
            ...(iCall
                ? [
                      {
                          name: 'session-request-received',
                          label: t('templates.sessionRequestReceived'),
                      },
                  ]
                : []),

            ...(iCalendar
                ? [
                      ...APPOINTMENT_TEMPLATES,
                      {
                          name: 'service-issues-agent',
                          label: t('templates.serviceIssuesAgent'),
                      },
                  ]
                : []),
            {
                name: 'password-reset',
                label: t('templates.passwordReset'),
            },
            {
                name: 'invitation-confirm',
                label: t('templates.invitationConfirm'),
            },
            {
                name: 'report-completed',
                label: t('templates.reportCompleted'),
            },
            {
                name: 'report-request-error',
                label: t('templates.reportRequestError'),
            },
            ...(iEvents || iStore
                ? [
                      {
                          name: 'ievent-invitation',
                          label: t('templates.iEventInvitation'),
                      },
                      {
                          name: 'ievent-reminder',
                          label: t('templates.iEventReminder'),
                      },
                      {
                          name: 'ievent-cancellation',
                          label: t('templates.iEventCancellation'),
                      },
                      {
                          name: 'ievent-survey',
                          label: t('templates.iEventSurvey'),
                      },
                      {
                          name: 'ievent-update',
                          label: t('templates.iEventUpdate'),
                      },
                      {
                          name: 'ievent-available-slot',
                          label: t('templates.iEventAvailableSlot'),
                      },
                      {
                          name: 'ievent-admin-registration',
                          label: t('templates.iEventAdminRegistration'),
                      },
                      {
                          name: 'ievent-broadcast',
                          label: t('templates.iEventBroadcast'),
                      },
                  ]
                : []),
            ...(leads
                ? [
                      {
                          name: 'new-contact-request',
                          label: t('settings.communications.new-contact-request'),
                      },
                    {
                          name: 'new-lost-opportunity',
                          label: t('settings.communications.new-lost-opportunity'),
                      },
                  ]
                : []),
            {
                name: 'user-session-confirm',
                label: t('templates.userSessionConfirm'),
            },
        ],
        [iLive, iCall, iCalendar, iEvents, iStore, leads, t, APPOINTMENT_TEMPLATES],
    );

    const SMS_TEMPLATES = useMemo(() => {
        const candidates = iCalendar ? EMAIL_TEMPLATES : [...EMAIL_TEMPLATES, ...APPOINTMENT_TEMPLATES];
        const seenNames = new Set();
        return candidates.filter((template) => {
            if (seenNames.has(template.name) || !SMS_SUPPORTED_TEMPLATE_NAMES.has(template.name)) {
                return false;
            }
            seenNames.add(template.name);
            return true;
        });
    }, [EMAIL_TEMPLATES, APPOINTMENT_TEMPLATES, iCalendar]);

    return [EMAIL_TEMPLATES, SMS_TEMPLATES];
}
